import React from "react";

/**
 * ListView — aranabilir liste sayfalarının ortak gövde organizması.
 * Atomic design: organizma (atom/molekülleri kompoze eder).
 *
 * Cihazlar / Firmalar / Kullanıcılar gibi liste sayfalarında tekrar eden
 * yapıyı tek yerde toplar:
 *   - Mobil kart scroll kabuğu (`md:hidden`)
 *   - Masaüstü tablo kabuğu (`hidden md:block`): sticky header + scroll
 *   - Yükleniyor / boş durum geçişleri
 *   - Opsiyonel footer (sayfalama)
 *
 * `scrollbar-gutter: stable` ile scrollbar belirince/kaybolunca oluşan
 * "header kayması" merkezî olarak burada çözülür.
 *
 * Başlık/filtre/arama gibi domain'e özel üst bar bu bileşenin DIŞINDA,
 * sayfanın kendisinde kalır — ListView yalnızca gövde + footer'ı yönetir.
 *
 * İki gövde deseni desteklenir:
 *   1) Kolon-config: `columns` + (ops.) `rowOverlay`/`rowClassName` — ListView
 *      her satırı `<tr>` olarak kendisi üretir.
 *   2) Satır-component: `renderRow(row,i)` tam bir `<tr>` döndürür (ör. FirmCard,
 *      renderDeviceCard). Bu verilince kolon body'si yok sayılır; başlıklar
 *      `headers` (yoksa `columns`) üzerinden çizilir.
 *
 * @param {React.Ref} [contentRef] - gövde (`flex-1 overflow-hidden`) div'ine ref
 * @param {Array<{label,className?}>} [headers] - başlık hücreleri (yoksa columns'tan türetilir)
 * @param {string} [headerClassName] - th temel className (varsayılan: Users stili)
 * @param {string} [tableClassName] - <table>'a ek className (ör. "table-fixed")
 * @param {Array<{key,label,headerClassName?,cellClassName?,render?}>} [columns]
 * @param {(row,i)=>React.ReactNode} [renderRow] - tam <tr> (satır-component deseni)
 * @param {Array} data
 * @param {string|(row,i)=>string} [rowKey="_id"]
 * @param {(row,i)=>string} [rowClassName] - kolon-config satır className
 * @param {(row,i)=>React.ReactNode} [rowOverlay] - ilk hücreye eklenen mutlak katman
 * @param {(row,i)=>React.ReactNode} [renderMobileCard]
 * @param {boolean} [isLoading]
 * @param {React.ReactNode} [loadingNode] - tüm gövdeyi kaplayan yükleme (ör. <ListSkeleton/>)
 * @param {React.ReactNode} [loadingDesktop] - masaüstü skeleton (tbody satırları)
 * @param {React.ReactNode} [loadingMobile] - mobil skeleton
 * @param {React.ReactNode} [empty] - veri boşken (yükleme değilken)
 * @param {React.ReactNode} [footer] - alt bar (ör. <Pagination/>)
 */
const scrollFix = { scrollbarGutter: "stable" };

// Tüm liste tablolarında ORTAK başlık tipografisi — filtre/arama kontrolleriyle
// aynı ölçek (text-xs sm:text-sm) kullanılır ki liste UI'ında font tutarlı olsun.
// NOT: CSS `uppercase` YOK — Türkçe casing'i bozar (İ → noktasız I). Etiketler
// zaten literal büyük harf yazılır.
const HEAD_BASE_DEFAULT =
  "px-4 py-3 text-left text-xs sm:text-sm font-semibold text-gray-500 tracking-wider bg-gray-50";
const thStickyStyle = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  backgroundColor: "#f9fafb",
};

export default function ListView({
  contentRef,
  headers,
  headerClassName = HEAD_BASE_DEFAULT,
  tableClassName = "",
  columns = [],
  renderRow,
  data = [],
  rowKey = "_id",
  rowClassName,
  rowOverlay,
  renderMobileCard,
  isLoading = false,
  loadingNode,
  loadingDesktop,
  loadingMobile,
  empty,
  footer,
  mobileListFooter,
}) {
  const getRowKey = (row, idx) => {
    if (typeof rowKey === "function") return rowKey(row, idx);
    return row?.[rowKey] ?? idx;
  };

  const isEmpty = !isLoading && (!data || data.length === 0);

  const headerList =
    headers ||
    columns.map((c) => ({ key: c.key, label: c.label, className: c.headerClassName }));

  const DesktopHead = (
    <thead
      className="bg-gray-50 sticky top-0 z-30 shadow-sm"
      style={{ position: "sticky", top: 0, zIndex: 30 }}
    >
      <tr>
        {headerList.map((h) => (
          <th
            key={h.key || h.label}
            className={`${headerClassName} ${h.className || ""}`}
            style={thStickyStyle}
          >
            {h.label}
          </th>
        ))}
      </tr>
    </thead>
  );

  const desktopBody = renderRow
    ? data.map((row, idx) => (
        <React.Fragment key={getRowKey(row, idx)}>{renderRow(row, idx)}</React.Fragment>
      ))
    : data.map((row, idx) => (
        <tr
          key={getRowKey(row, idx)}
          className={
            rowClassName ? rowClassName(row, idx) : "bg-white hover:bg-gray-50 transition-colors"
          }
        >
          {columns.map((col, ci) => (
            <td key={col.key} className={`px-4 py-3 ${col.cellClassName || ""}`}>
              {ci === 0 && rowOverlay ? rowOverlay(row, idx) : null}
              {col.render ? col.render(row, idx) : row?.[col.key] ?? (
                <span className="text-gray-400">—</span>
              )}
            </td>
          ))}
        </tr>
      ));

  return (
    <>
      <div ref={contentRef} className="flex-1 overflow-hidden">
        {isLoading ? (
          loadingNode ? (
            loadingNode
          ) : (
            <>
              <div className="md:hidden h-full overflow-auto" style={scrollFix}>
                {loadingMobile}
              </div>
              <div className="hidden md:block h-full overflow-auto" style={scrollFix}>
                <table className={`min-w-full divide-y divide-gray-200 ${tableClassName}`}>
                  {DesktopHead}
                  <tbody className="divide-y divide-gray-100">{loadingDesktop}</tbody>
                </table>
              </div>
            </>
          )
        ) : isEmpty ? (
          empty
        ) : (
          <>
            {/* Mobil kartlar */}
            <div className="md:hidden h-full overflow-auto" style={scrollFix}>
              {renderMobileCard &&
                data.map((row, idx) => (
                  <React.Fragment key={getRowKey(row, idx)}>
                    {renderMobileCard(row, idx)}
                  </React.Fragment>
                ))}
              {/* Scroll alanının İÇİNDE render edilen alt bölüm — sonsuz
                  kaydırma sentinel'i gibi listeyle akan içerikler için. */}
              {mobileListFooter}
            </div>

            {/* Masaüstü tablo */}
            <div className="hidden md:block h-full overflow-auto" style={scrollFix}>
              <table className={`min-w-full divide-y divide-gray-200 ${tableClassName}`}>
                {DesktopHead}
                <tbody className="divide-y divide-gray-100">{desktopBody}</tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {footer}
    </>
  );
}
